import React, { useState, useEffect } from 'react';
import { X, Smartphone, Share, MoreVertical, Plus, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';

const INSTALL_HELP_DISMISSED_KEY = 'ofs_install_help_dismissed';

// Helper functions to check device state (called outside useEffect)
const checkIsStandalone = () => {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(display-mode: standalone)').matches 
    || window.navigator.standalone;
};

const checkDeviceType = () => {
  if (typeof navigator === 'undefined') return { isIOS: false, isAndroid: false };
  const userAgent = navigator.userAgent.toLowerCase();
  return {
    isIOS: /iphone|ipad|ipod/.test(userAgent),
    isAndroid: /android/.test(userAgent)
  };
};

export const InstallHelpBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const isStandalone = checkIsStandalone();
  const { isIOS, isAndroid } = checkDeviceType();

  useEffect(() => {
    if (isStandalone) return;

    // Only show on mobile devices that haven't dismissed
    const dismissed = localStorage.getItem(INSTALL_HELP_DISMISSED_KEY);
    const isMobile = isIOS || isAndroid;
    
    if (isMobile && !dismissed) {
      // Show after a delay
      const timer = setTimeout(() => setIsVisible(true), 2000);
      return () => clearTimeout(timer);
    }
  }, [isStandalone, isIOS, isAndroid]);

  const handleDismiss = () => {
    localStorage.setItem(INSTALL_HELP_DISMISSED_KEY, 'true');
    setIsVisible(false);
  };

  if (!isVisible || isStandalone) return null;

  return (
    <div 
      className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-4 py-3 shadow-lg animate-in slide-in-from-top duration-300"
      data-testid="install-help-banner"
    >
      <div className="max-w-4xl mx-auto flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-white/20 rounded-lg flex-shrink-0">
            <Smartphone className="h-5 w-5" />
          </div>
          
          <div className="text-sm">
            <p className="font-semibold">Install Option Wizard App</p>
            {isIOS ? (
              <p className="text-white/80 text-xs flex items-center gap-1 flex-wrap">
                Tap <Share className="h-3 w-3 inline" /> Share → <Plus className="h-3 w-3 inline" /> Add to Home Screen
              </p>
            ) : isAndroid ? (
              <p className="text-white/80 text-xs flex items-center gap-1 flex-wrap">
                Tap <MoreVertical className="h-3 w-3 inline" /> Menu → <Download className="h-3 w-3 inline" /> Install app
              </p>
            ) : (
              <p className="text-white/80 text-xs">Add to home screen for the best experience</p>
            )}
          </div>
        </div>

        <Button
          variant="ghost"
          size="sm"
          onClick={handleDismiss}
          className="text-white hover:bg-white/20 p-1 h-auto flex-shrink-0"
          data-testid="install-help-dismiss"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export default InstallHelpBanner;
